import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useRef } from 'react';
import { RotateCcw } from 'lucide-react';
// Compact value pill. Clicking it expands/collapses the row's SliderUnderBar
// below (the row extends downward and pushes siblings, Figma-panel style),
// replacing the old portal popover that floated over the rows beneath.
export const SliderChip = ({ label, value, formatLabel, expanded, onToggle, }) => {
    return (_jsx("button", { type: "button", "data-slider-chip": "true", onClick: (e) => {
            e.stopPropagation();
            onToggle();
        }, className: "px-1.5 py-0.5 rounded text-[10px] font-semibold flex-shrink-0 transition-colors", style: {
            // Resting state must stay legible on the dark row: --dew text with a
            // pewter border. Pewter text (#4E574E) vanishes on gray-800. The sand
            // fill appears only while this chip's under-bar is open.
            backgroundColor: expanded ? 'var(--sand)' : 'transparent',
            border: `1px solid ${expanded ? 'var(--sand)' : 'var(--pewter)'}`,
            color: expanded ? 'var(--forest)' : 'var(--dew)',
        }, title: `Adjust ${label.toLowerCase()}`, "aria-label": `Adjust ${label.toLowerCase()}`, "aria-expanded": expanded, children: formatLabel(value) }));
};
// Generic row extension: a full-width bar rendered by a layer row directly
// beneath its controls, pushing the rows below down instead of overlaying them.
// Owns the dismissal contract shared by every extension (sliders, monochrome):
// Esc or clicking anywhere outside collapses it, while clicks on any
// [data-slider-chip] element are left alone so chips keep their own toggle
// semantics (switching straight from one extension to another).
export const RowUnderBar = ({ onClose, children, className }) => {
    const barRef = useRef(null);
    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === 'Escape')
                onClose();
        };
        const handleMouseDown = (event) => {
            var _a;
            const target = event.target;
            if ((_a = barRef.current) === null || _a === void 0 ? void 0 : _a.contains(target))
                return;
            if (target.closest('[data-slider-chip]'))
                return;
            onClose();
        };
        document.addEventListener('keydown', handleKeyDown);
        document.addEventListener('mousedown', handleMouseDown, true);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            document.removeEventListener('mousedown', handleMouseDown, true);
        };
    }, [onClose]);
    return (_jsx("div", { ref: barRef, className: `ls-underbar mt-2 px-2 py-1.5 rounded ${className !== null && className !== void 0 ? className : ''}`, style: { backgroundColor: 'rgba(0, 0, 0, 0.25)' }, onClick: (e) => e.stopPropagation(), children: children }));
};
// The slider flavour of RowUnderBar. The range input is autofocused so arrow
// keys adjust immediately, and a reset affordance appears once the value has
// left its default.
export const SliderUnderBar = ({ label, value, defaultValue, min, max, step, formatLabel, onChange, onClose, minIcon, maxIcon, }) => {
    const modified = Math.abs(value - defaultValue) > 1e-6;
    return (_jsxs(RowUnderBar, { onClose: onClose, className: "flex items-center gap-2", children: [_jsx("span", { className: "text-[10px] font-semibold uppercase tracking-wide text-gray-400 flex-shrink-0", children: label }), minIcon, _jsx("input", { type: "range", autoFocus: true, min: min, max: max, step: step, value: value, onChange: (e) => onChange(parseFloat(e.target.value)), className: "flex-1 h-1 bg-gray-600 rounded-lg appearance-none cursor-pointer slider" }), maxIcon, _jsx("span", { className: "text-[10px] tabular-nums text-gray-300 flex-shrink-0 w-9 text-right", children: formatLabel(value) }), _jsx("button", { type: "button", onClick: () => onChange(defaultValue), disabled: !modified, className: "w-5 h-5 rounded flex items-center justify-center flex-shrink-0 transition-opacity hover:bg-gray-700/60", style: { opacity: modified ? 0.8 : 0.25, color: 'var(--dew)' }, title: `Reset to ${formatLabel(defaultValue)}`, "aria-label": `Reset ${label.toLowerCase()} to ${formatLabel(defaultValue)}`, children: _jsx(RotateCcw, { size: 11 }) })] }));
};
